import { Node } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state"; // Include TextSelection here
import React from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { debounce } from "throttle-debounce";

// Utility function to highlight non-matching words in bold
const getHighlightedText = (suggestion, textareaContent) => {
  const suggestionWords = suggestion.split(" ");
  const textareaWords = textareaContent ? textareaContent.split(" ") : [];

  return suggestionWords.map((word, index) =>
    textareaWords.includes(word.toLowerCase()) ? (
      <span key={index}>{word} </span> // No bold for matching words
    ) : (
      <strong key={index}>{word} </strong> // Bold for non-matching words
    )
  );
};

const SuggestionsDropdown = ({
  suggestions,
  selectedIndex,
  onSuggestionClick,
  onArrowKeyDown,
  textareaContent,
}) => {
  // Don't render if no suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className="suggestions-container"
      style={{
        backgroundColor: "white",
        border: "none",
        maxHeight: "190px",
        overflowY: "auto",
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "1.5rem", // Match the rounded corners
      }}
    >
      {suggestions.map((suggestion, index) => (
        <div
          key={index}
          style={{
            padding: "10px 15px", // Consistent padding
            cursor: "pointer",
            backgroundColor: index === selectedIndex ? "#f5f5f5" : "white", // Match hover/selected style
            width: "100%",
            boxSizing: "border-box",
            fontSize: "16px", // Readable font size
            lineHeight: "1.4",
            borderBottom:
              index < suggestions.length - 1 ? "1px solid #eee" : "none", // Divider between items
          }}
          onClick={() => {
            // Treat click the same as pressing Enter
            onSuggestionClick(suggestion, true);
            // Scroll to bottom of the text area after selection
            const proseMirror = document.querySelector(".ProseMirror");
            if (proseMirror) {
              proseMirror.scrollTop = proseMirror.scrollHeight;
            }
          }}
          onMouseEnter={() => onArrowKeyDown(index)}
        >
          {getHighlightedText(suggestion.text_entry, textareaContent)}
        </div>
      ))}
    </div>
  );
};

// Tiptap extension definition
const Autocomplete = Node.create({
  name: "autocomplete",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("autocomplete"),
        view(editorView) {
          return new AutocompleteView(editorView);
        },
      }),
    ];
  },
});

class AutocompleteView {
  constructor(editorView) {
    this.editorView = editorView;
    this.suggestionsContainer = document.createElement("div");
    this.suggestionsContainer.className = "autocomplete-suggestions";
    this.suggestionsContainer.style.zIndex = 1000;
    this.suggestionsContainer.style.display = "none"; // Initially hidden
    this.suggestionsContainer.style.position = "absolute";

    // Append to the editor wrapper to ensure correct positioning
    const editorWrapper =
      this.editorView.dom.closest(".editor-wrapper") ||
      this.editorView.dom.parentNode;
    editorWrapper.style.position = "relative"; // Ensure parent has relative positioning
    editorWrapper.appendChild(this.suggestionsContainer);

    this.update = this.update.bind(this);
    this.fetchSuggestions = debounce(300, this.fetchSuggestions.bind(this));
    this.handleClickOutside = this.handleClickOutside.bind(this);

    // Initialize state tracking
    this.suggestionStates = [];
    this.lastAcceptedPosition = 0; // Track the end position of the last accepted suggestion
    this.selectedIndex = -1;
    this.suggestions = [];
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTextInput = this.onTextInput.bind(this);

    // Add keydown listener for arrow key navigation
    this.editorView.dom.addEventListener("keydown", this.onKeyDown);
    this.editorView.dom.addEventListener("input", this.onTextInput);
    document.addEventListener("click", this.handleClickOutside);

    // Check if the editor already has content on initialization
    const initialContent = this.editorView.state.doc.textContent;
    if (initialContent.trim() !== "") {
      // If there's already content, set lastAcceptedPosition to prevent suggestions until cleared
      this.lastAcceptedPosition = initialContent.length;
    }
  }

  onTextInput(event) {
    // Only show suggestions when the editor was previously empty (no searches performed yet)
    const fullText = this.editorView.state.doc.textContent;
    // Check if this is the first text input (lastAcceptedPosition is 0)
    if (this.lastAcceptedPosition === 0 && fullText.trim() !== "") {
      this.fetchSuggestions();
    }
  }

  handleClickOutside(event) {
    // Hide suggestions when clicking outside the editor or suggestions container
    if (
      this.suggestionsContainer &&
      !this.suggestionsContainer.contains(event.target) &&
      !this.editorView.dom.contains(event.target)
    ) {
      this.hideSuggestions();
    }
  }

  onKeyDown(event) {
    if (this.suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length;
      this.renderSuggestions();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.selectedIndex =
        (this.selectedIndex - 1 + this.suggestions.length) %
        this.suggestions.length;
      this.renderSuggestions();
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (this.selectedIndex >= 0) {
        this.insertSuggestion(this.suggestions[this.selectedIndex], true);
      }
    } else if (event.key === "Escape") {
      this.hideSuggestions();
    }
  }

  // Fetch suggestions from API based on the new text
  fetchSuggestions() {
    const fullText = this.editorView.state.doc.textContent;

    // Determine the text after the last accepted suggestion
    const newText = fullText.slice(this.lastAcceptedPosition);

    // Reset lastAcceptedPosition if the editor is empty to allow suggestions again
    if (fullText.trim() === "") {
      this.lastAcceptedPosition = 0;
      this.suggestionStates = []; // Clear suggestion states when editor is empty
    }

    // Allow suggestions even if there's previous content
    if (!newText.trim()) {
      this.suggestions = [];
      this.hideSuggestions();
      return; // No need to fetch suggestions if there's no new text
    }

    const query = {
      min_score: 0.1,
      query: {
        function_score: {
          query: {
            bool: {
              must: [],
              should: [
                {
                  multi_match: {
                    query: newText,
                    type: "bool_prefix",
                    fields: [
                      "text_entry.as_you_type",
                      "text_entry.as_you_type._2gram",
                      "text_entry.as_you_type._3gram",
                    ],
                  },
                },
                {
                  match: {
                    text_entry: {
                      query: newText,
                      analyzer: "standard",
                    },
                  },
                },
                {
                  match_phrase_prefix: {
                    text_entry: {
                      query: newText,
                      slop: 5,
                    },
                  },
                },
                {
                  function_score: {
                    query: {
                      match_all: {},
                    },
                    functions: [
                      {
                        filter: {
                          match: {
                            text_entry_completion: {
                              query: newText,
                            },
                          },
                        },
                        weight: 2,
                      },
                    ],
                    boost_mode: "multiply",
                  },
                },
              ],
              minimum_should_match: 1,
            },
          },
          boost_mode: "sum",
        },
      },
      size: 5,
    };

    axios
      .post("https://auto.ciqler.com:9201/searchesv2/_search", query, {
        headers: {
          "Content-Type": "application/json",
        },
        auth: {
          username: "admin",
          password: "Ciqler@24auto",
        },
      })
      .then((response) => {
        this.suggestions = response.data.hits.hits.map((hit) => ({
          ...hit._source,
          _query: newText, // Only new text is sent to the API
        }));

        // Only render if there are suggestions
        if (this.suggestions.length > 0) {
          this.renderSuggestions();
        } else {
          this.hideSuggestions();
        }
      })
      .catch((error) => {
        console.error("Error fetching suggestions:", error);
        this.suggestions = [];
        this.hideSuggestions();
      });
  }

  // Update the plugin state
  update(view, lastState) {
    const { state, composing } = view;
    if (state !== lastState || composing) {
      // We'll rely on input event instead for more responsive behavior
    }
  }

  // Render the suggestions
  renderSuggestions() {
    const { state } = this.editorView;
    const selection = state.selection;

    // Only show suggestions if there are any
    if (this.suggestions.length === 0) {
      this.hideSuggestions();
      return;
    }

    // Get the position of the cursor
    const coords = this.editorView.coordsAtPos(selection.from);

    // Calculate the editor wrapper dimensions
    const editorWrapper = this.editorView.dom.closest(".editor-wrapper");
    if (!editorWrapper) {
      this.hideSuggestions();
      return;
    }

    const wrapperRect = editorWrapper.getBoundingClientRect();

    // Set the position of the suggestions container to match the search area
    this.suggestionsContainer.style.position = "fixed";
    this.suggestionsContainer.style.left = `${wrapperRect.left}px`;
    this.suggestionsContainer.style.top = `${wrapperRect.top - 240}px`; // Position above the editor (200px height + 10px gap)
    this.suggestionsContainer.style.width = `${wrapperRect.width}px`; // Dynamic width to match the editor
    this.suggestionsContainer.style.boxSizing = "border-box";

    // Apply styling to match search area
    this.suggestionsContainer.style.border = "1px solid #AD1700"; // Red border like search area
    this.suggestionsContainer.style.borderRadius = "1.5rem"; // Rounded corners like search area
    this.suggestionsContainer.style.boxShadow =
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
    this.suggestionsContainer.style.maxHeight = "200px";
    this.suggestionsContainer.style.overflowY = "auto";
    this.suggestionsContainer.style.padding = "5px";
    this.suggestionsContainer.style.backgroundColor = "white";
    this.suggestionsContainer.style.zIndex = "1000";
    this.suggestionsContainer.style.display = "block"; // Show the container
    this.suggestionsContainer.style.visibility = "visible"; // Ensure visibility
    this.suggestionsContainer.style.opacity = "1"; // Ensure opacity

    ReactDOM.render(
      <SuggestionsDropdown
        suggestions={this.suggestions}
        selectedIndex={this.selectedIndex}
        onSuggestionClick={(suggestion) => this.insertSuggestion(suggestion, false)}
        onArrowKeyDown={(index) => this.updateSelectedIndex(index)}
        textareaContent={this.editorView.state.doc.textContent}
      />,
      this.suggestionsContainer
    );
  }

  // Update the selected index and re-render suggestions
  updateSelectedIndex(index) {
    this.selectedIndex = index;
    this.renderSuggestions();
  }

  // Insert suggestion into the editor
  insertSuggestion(suggestion, isEnterKey = false) {
    const { state, dispatch } = this.editorView;

    // Get the current selection position
    const cursorPosition = state.selection.from;

    // Determine the start position of the text to be replaced
    const startPosition =
      this.suggestionStates.length > 0
        ? this.suggestionStates[this.suggestionStates.length - 1].end
        : 0;

    // Extract the text preceding the cursor
    const precedingText = state.doc.textBetween(
      Math.max(0, startPosition - 1),
      startPosition
    );

    // Check if there is a trailing space before the new text, and add one if not
    const suggestionText =
      (!precedingText.endsWith(" ") ? " " : "") + suggestion.text_entry;

    // Create a transaction to replace text and set cursor position
    const transaction = state.tr
      .delete(startPosition, cursorPosition) // Remove the existing partial text
      .insertText(suggestionText, startPosition); // Insert suggestion with space if needed

    const newCursorPosition = startPosition + suggestionText.length + 1;
    transaction.setSelection(
      TextSelection.create(transaction.doc, newCursorPosition)
    );

    dispatch(transaction);

    // Focus the editor after inserting the suggestion
    this.editorView.focus();

    // Set lastAcceptedPosition to prevent further suggestions
    this.lastAcceptedPosition = newCursorPosition;

    // Save the new suggestion state
    this.suggestionStates.push({
      start: startPosition,
      end: this.lastAcceptedPosition,
    });

    // Hide the suggestions after insertion
    this.hideSuggestions();

    // Clear suggestions to prevent them from appearing again
    this.suggestions = [];

    // Trigger search after inserting suggestion (both for click and Enter)
    setTimeout(() => {
      const searchButton = document.querySelector('[data-search-button]') || 
                          document.querySelector('button[type="submit"]') ||
                          document.querySelector('#search-button');
      if (searchButton) {
        searchButton.click();
      } else {
        // Fallback: dispatch a custom event that Search component can listen to
        const searchEvent = new CustomEvent('autocomplete-search-trigger', {
          detail: { searchText: suggestionText.trim() }
        });
        document.dispatchEvent(searchEvent);
      }
    }, 100);
  }

  // Hide the suggestions dropdown
  hideSuggestions() {
    if (this.suggestionsContainer) {
      ReactDOM.unmountComponentAtNode(this.suggestionsContainer);
      this.suggestionsContainer.style.display = "none";
    }
    this.suggestions = [];
    this.selectedIndex = -1;
  }

  destroy() {
    ReactDOM.unmountComponentAtNode(this.suggestionsContainer);
    this.suggestionsContainer.remove();
    this.editorView.dom.removeEventListener("keydown", this.onKeyDown);
    this.editorView.dom.removeEventListener("input", this.onTextInput);
    document.removeEventListener("click", this.handleClickOutside);
  }
}

export default Autocomplete;
